import { CbcmUtils, MOCK_CBCM_MFOLFOX6 as MOCK_CBCM_MFOLFOX6_DOC } from '@cyan-blue/cbcm';
import type {
    Adapter,
    AdapterSummaryStats,
    CbcmSchemaSection,
    SampleRegimenOption
} from './adapters.model';

export const TRIFOUR_DEMO_MODE = false;

/** Kept in data but hidden from the Trifour demo registry view. */
export const TRIFOUR_DEMO_HIDDEN_ADAPTER_IDS = [
    'adapter-cerner',
    'adapter-epic',
    'adapter-fhir',
    'adapter-hl7'
] as const;

export const TRIMED_JSONATA_EXPRESSION =
`{
  "triMedRegimen": {
    "regimenId": id,
    "regimenName": regimenName,
    "description": fullRegimenName,
    "diseaseSite": diseaseSite,
    "intent": intent,
    "cycleDays": cycleFrequency.intervalDays,
    "orderSets": drugRegimen.{
      "sequence": order,
      "medication": drug,
      "dose": dose,
      "route": route,
      "timing": timing
    },
    "metadata": {
      "sourceAuthority": source,
      "sourceVersion": version,
      "exportedBy": "Cyan Blue",
      "exportedOn": $now()
    }
  }
}`;

export const CERNER_JSONATA_EXPRESSION =
`{
  "PowerPlan": {
    "PlanName": regimenName,
    "Description": fullRegimenName,
    "DiseaseCategory": diseaseSite,
    "CancerTypes": cancerTypes,
    "Intent": intent,
    "CycleLength": cycleFrequency
      .intervalDays & " days",
    "ContinuationCriteria":
      cycleFrequency.continuationCriteria,
    "Components": drugRegimen.{
      "Sequence": order,
      "ComponentName": drug,
      "Dose": dose,
      "Route": route,
      "Duration": administrationDuration,
      "Timing": timing,
      "Notes": notes
    },
    "Monitoring": clinicalMonitoring.{
      "Parameter": item,
      "Frequency": timing,
      "Category": category
    },
    "AdminInfo": {
      "VisitDuration":
        administrativeInfo
          .approximateVisitDuration,
      "PharmacyWorkload":
        administrativeInfo
          .pharmacyWorkloadMinutes
          & " min",
      "NursingWorkload":
        administrativeInfo
          .nursingWorkloadMinutes
          & " min"
    },
    "Metadata": {
      "SourceAuthority": source,
      "SourceVersion": version,
      "CBCMVersion": "1.0",
      "ExportedBy": "Cyan Blue",
      "ExportedOn": $now(),
      "RegimenId": id
    }
  }
}`;

export const EPIC_JSONATA_EXPRESSION =
`{
  "BeaconProtocol": {
    "ProtocolName": regimenName,
    "FullName": fullRegimenName,
    "TumorGroup": diseaseSite,
    "Diagnoses": cancerTypes,
    "TreatmentIntent": intent,
    "CycleDays":
      cycleFrequency.intervalDays,
    "Drugs": drugRegimen.{
      "DrugName": drug,
      "DosePerMeterSquared":
        $number($split(dose, " ")[0]),
      "DoseUnit":
        $split(dose, " ")[1],
      "Route": route,
      "InfusionDuration":
        administrationDuration,
      "DayOfCycle": timing,
      "OrderSequence": order
    },
    "LabMonitoring":
      clinicalMonitoring[
        category = "Required"
      ].{
        "Test": item,
        "Schedule": timing
      },
    "Source": {
      "Authority": source,
      "Version": version,
      "ImportDate": $now()
    }
  }
}`;

export const FHIR_JSONATA_EXPRESSION =
`{
  "resourceType": "Bundle",
  "type": "collection",
  "id": id,
  "entry": [
    {
      "resource": {
        "resourceType":
          "PlanDefinition",
        "id": id,
        "title": regimenName,
        "description": fullRegimenName,
        "status": "active",
        "subjectCodeableConcept": {
          "coding": [{
            "system":
              "http://snomed.info/sct",
            "display": diseaseSite
          }]
        },
        "action": drugRegimen.{
          "title": drug,
          "description": "Dose: "
            & dose & " | Route: "
            & route,
          "timingTiming": {
            "repeat": {
              "duration":
                $number($split(
                  administrationDuration,
                  " ")[0]),
              "durationUnit": "h"
            }
          }
        }
      }
    }
  ],
  "meta": {
    "source": "Cyan Blue",
    "lastUpdated": $now()
  }
}`;

export const HL7_JSONATA_EXPRESSION =
`"MSH|^~\&|CyanBlue|" &
source & "|TargetHIS||" &
$replace($now(), /[^0-9]/, "") &
"||ORM^O01|" & id &
"|P|2.5|||NE|NE\rPID|||" &
regimenName & "^^^" &
diseaseSite & "\r" &
$join(drugRegimen.("ORC|NW|||||||" &
$now() & "||||\rRXO|" &
drug & "^" & dose & "^" &
route & "|||" & timing), "\r")`;

export const MOCK_ADAPTERS: Adapter[] = [
  {
    id: 'adapter-cerner',
    name: 'Cerner PowerPlan Adapter',
    description: 'Transforms CBCM regimen ' +
      'objects into Cerner PowerPlan XML ' +
      'component definitions. Supports ' +
      'all drug regimen components, ' +
      'monitoring parameters, and ' +
      'administrative metadata.',
    targetSystem: 'Cerner',
    outputFormat: 'XML',
    status: 'Active',
    currentVersion: '1.2.1',
    currentExpression: CERNER_JSONATA_EXPRESSION,
    regimensDeployed: 18,
    testStatus: 'Passing',
    testsPassing: 12,
    testsTotal: 12,
    createdBy: 'HIS Admin',
    createdOn: new Date('2024-01-10'),
    lastUpdatedBy: 'HIS Admin',
    lastUpdatedOn: new Date(
      Date.now() - 14 * 86400000),
    tags: ['Production', 'Oncology'],
    testCases: [
      {
        id: 'tc-cerner-001',
        name: 'MFOLFOX6 basic transform',
        description: 'Validates full ' +
          'MFOLFOX6 regimen transforms ' +
          'correctly to PowerPlan format',
        sampleRegimenId:
          'rrg-mfolfox6-cco-2023',
        sampleRegimenName: 'MFOLFOX6',
        expectedOutputPath:
          'PowerPlan.PlanName',
        expectedOutputValue: 'MFOLFOX6',
        status: 'Passing',
        lastRunOn: new Date(
          Date.now() - 86400000),
        lastRunDuration: 142
      },
      {
        id: 'tc-cerner-002',
        name: 'Drug component mapping',
        description: 'Validates all 4 ' +
          'MFOLFOX6 drug components ' +
          'map correctly',
        sampleRegimenId:
          'rrg-mfolfox6-cco-2023',
        sampleRegimenName: 'MFOLFOX6',
        expectedOutputPath:
          'PowerPlan.Components[0]' +
          '.ComponentName',
        expectedOutputValue: 'Oxaliplatin',
        status: 'Passing',
        lastRunOn: new Date(
          Date.now() - 86400000),
        lastRunDuration: 156
      },
      {
        id: 'tc-cerner-003',
        name: 'Cycle frequency mapping',
        description: 'Validates cycle ' +
          'length maps to "14 days"',
        sampleRegimenId:
          'rrg-mfolfox6-cco-2023',
        sampleRegimenName: 'MFOLFOX6',
        expectedOutputPath:
          'PowerPlan.CycleLength',
        expectedOutputValue: '14 days',
        status: 'Passing',
        lastRunOn: new Date(
          Date.now() - 86400000),
        lastRunDuration: 98
      }
    ],
    versions: [
      {
        id: 'v-cerner-3',
        versionNumber: '1.2.1',
        expression: CERNER_JSONATA_EXPRESSION,
        createdBy: 'HIS Admin',
        createdOn: new Date(
          Date.now() - 14 * 86400000),
        status: 'Active',
        changelog: 'Added AdminInfo block ' +
          'with pharmacy and nursing ' +
          'workload fields',
        testStatus: 'Passing',
        testsPassing: 12,
        testsTotal: 12,
        isActive: true
      },
      {
        id: 'v-cerner-2',
        versionNumber: '1.2.0',
        expression: CERNER_JSONATA_EXPRESSION,
        createdBy: 'HIS Admin',
        createdOn: new Date(
          Date.now() - 30 * 86400000),
        status: 'Deprecated',
        changelog: 'Added monitoring ' +
          'parameters mapping',
        testStatus: 'Passing',
        testsPassing: 10,
        testsTotal: 10,
        isActive: false
      },
      {
        id: 'v-cerner-1',
        versionNumber: '1.0.0',
        expression: '{ "PowerPlan": ' +
          '{ "PlanName": regimenName } }',
        createdBy: 'HIS Admin',
        createdOn: new Date(
          Date.now() - 90 * 86400000),
        status: 'Deprecated',
        changelog: 'Initial version — ' +
          'basic name mapping only',
        testStatus: 'Passing',
        testsPassing: 3,
        testsTotal: 3,
        isActive: false
      }
    ]
  },
  {
    id: 'adapter-epic',
    name: 'Epic Beacon Adapter',
    description: 'Transforms CBCM regimen ' +
      'objects into Epic Beacon regimen ' +
      'JSON format. Maps drug orders, ' +
      'tumor group, and lab monitoring ' +
      'to Epic-compatible structure.',
    targetSystem: 'Epic',
    outputFormat: 'JSON',
    status: 'Active',
    currentVersion: '2.0.0',
    currentExpression: EPIC_JSONATA_EXPRESSION,
    regimensDeployed: 6,
    testStatus: 'Passing',
    testsPassing: 8,
    testsTotal: 8,
    createdBy: 'HIS Admin',
    createdOn: new Date('2024-02-01'),
    lastUpdatedBy: 'HIS Admin',
    lastUpdatedOn: new Date(
      Date.now() - 30 * 86400000),
    tags: ['Production', 'Oncology'],
    testCases: [
      {
        id: 'tc-epic-001',
        name: 'MFOLFOX6 Beacon transform',
        description: 'Validates MFOLFOX6 ' +
          'maps to Beacon regimen format',
        sampleRegimenId:
          'rrg-mfolfox6-cco-2023',
        sampleRegimenName: 'MFOLFOX6',
        expectedOutputPath:
          'BeaconProtocol.ProtocolName',
        expectedOutputValue: 'MFOLFOX6',
        status: 'Passing',
        lastRunOn: new Date(
          Date.now() - 2 * 86400000),
        lastRunDuration: 187
      },
      {
        id: 'tc-epic-002',
        name: 'Dose numeric extraction',
        description: 'Validates dose is ' +
          'extracted as number not string',
        sampleRegimenId:
          'rrg-mfolfox6-cco-2023',
        sampleRegimenName: 'MFOLFOX6',
        expectedOutputPath:
          'BeaconProtocol.Drugs[0]' +
          '.DosePerMeterSquared',
        expectedOutputValue: '85',
        status: 'Passing',
        lastRunOn: new Date(
          Date.now() - 2 * 86400000),
        lastRunDuration: 203
      }
    ],
    versions: [
      {
        id: 'v-epic-2',
        versionNumber: '2.0.0',
        expression: EPIC_JSONATA_EXPRESSION,
        createdBy: 'HIS Admin',
        createdOn: new Date(
          Date.now() - 30 * 86400000),
        status: 'Active',
        changelog: 'Complete rewrite to ' +
          'match Epic Beacon v8.2 schema. ' +
          'Added dose unit extraction ' +
          'and lab monitoring filter.',
        testStatus: 'Passing',
        testsPassing: 8,
        testsTotal: 8,
        isActive: true
      },
      {
        id: 'v-epic-1',
        versionNumber: '1.0.0',
        expression: '{ "BeaconProtocol": ' +
          '{ "ProtocolName": regimenName } }',
        createdBy: 'HIS Admin',
        createdOn: new Date(
          Date.now() - 90 * 86400000),
        status: 'Deprecated',
        changelog: 'Initial version',
        testStatus: 'Passing',
        testsPassing: 2,
        testsTotal: 2,
        isActive: false
      }
    ]
  },
  {
    id: 'adapter-fhir',
    name: 'FHIR R4 MedicationRequest Adapter',
    description: 'Transforms CBCM regimen ' +
      'objects into HL7 FHIR R4 Bundle ' +
      'containing PlanDefinition and ' +
      'MedicationRequest resources. ' +
      'Supports SMART on FHIR integration.',
    targetSystem: 'FHIR',
    outputFormat: 'FHIR',
    status: 'Draft',
    currentVersion: '0.3.0',
    currentExpression: FHIR_JSONATA_EXPRESSION,
    regimensDeployed: 0,
    testStatus: 'Failing',
    testsPassing: 3,
    testsTotal: 6,
    createdBy: 'HIS Admin',
    createdOn: new Date('2024-03-01'),
    lastUpdatedBy: 'HIS Admin',
    lastUpdatedOn: new Date(
      Date.now() - 3 * 86400000),
    tags: ['Draft', 'FHIR'],
    testCases: [
      {
        id: 'tc-fhir-001',
        name: 'Bundle resource type',
        description: 'Validates output ' +
          'is a FHIR Bundle',
        sampleRegimenId:
          'rrg-mfolfox6-cco-2023',
        sampleRegimenName: 'MFOLFOX6',
        expectedOutputPath: 'resourceType',
        expectedOutputValue: 'Bundle',
        status: 'Passing',
        lastRunOn: new Date(
          Date.now() - 3 * 86400000),
        lastRunDuration: 211
      },
      {
        id: 'tc-fhir-002',
        name: 'PlanDefinition entry',
        description: 'Validates first ' +
          'entry is PlanDefinition',
        sampleRegimenId:
          'rrg-mfolfox6-cco-2023',
        sampleRegimenName: 'MFOLFOX6',
        expectedOutputPath:
          'entry[0].resource.resourceType',
        expectedOutputValue:
          'PlanDefinition',
        status: 'Passing',
        lastRunOn: new Date(
          Date.now() - 3 * 86400000),
        lastRunDuration: 198
      },
      {
        id: 'tc-fhir-003',
        name: 'MedicationRequest resources',
        description: 'Validates drug ' +
          'components map to ' +
          'MedicationRequest resources',
        sampleRegimenId:
          'rrg-mfolfox6-cco-2023',
        sampleRegimenName: 'MFOLFOX6',
        expectedOutputPath:
          'entry[1].resource.resourceType',
        expectedOutputValue:
          'MedicationRequest',
        status: 'Failing',
        lastRunOn: new Date(
          Date.now() - 3 * 86400000),
        lastRunDuration: 234,
        errorMessage:
          'Expected MedicationRequest ' +
          'but got undefined. Drug ' +
          'components not mapped to ' +
          'separate FHIR entries.'
      },
      {
        id: 'tc-fhir-004',
        name: 'SNOMED coding',
        description: 'Validates disease ' +
          'site maps to SNOMED coding',
        sampleRegimenId:
          'rrg-mfolfox6-cco-2023',
        sampleRegimenName: 'MFOLFOX6',
        expectedOutputPath:
          'entry[0].resource' +
          '.subjectCodeableConcept' +
          '.coding[0].system',
        expectedOutputValue:
          'http://snomed.info/sct',
        status: 'Failing',
        lastRunOn: new Date(
          Date.now() - 3 * 86400000),
        lastRunDuration: 189,
        errorMessage:
          'SNOMED system URI not found ' +
          'in output. Coding block ' +
          'structure mismatch.'
      }
    ],
    versions: [
      {
        id: 'v-fhir-1',
        versionNumber: '0.3.0',
        expression: FHIR_JSONATA_EXPRESSION,
        createdBy: 'HIS Admin',
        createdOn: new Date(
          Date.now() - 3 * 86400000),
        status: 'Draft',
        changelog: 'Added PlanDefinition ' +
          'action mapping for drug ' +
          'components',
        testStatus: 'Failing',
        testsPassing: 3,
        testsTotal: 6,
        isActive: true
      }
    ]
  },
  {
    id: 'adapter-hl7',
    name: 'HL7 v2.5 ORM Adapter',
    description: 'Transforms CBCM regimen ' +
      'objects into HL7 v2.5 ORM^O01 ' +
      'message format for legacy HIS ' +
      'systems that do not support FHIR.',
    targetSystem: 'HL7',
    outputFormat: 'HL7',
    status: 'Draft',
    currentVersion: '0.1.0',
    currentExpression: HL7_JSONATA_EXPRESSION,
    regimensDeployed: 0,
    testStatus: 'NotTested',
    testsPassing: 0,
    testsTotal: 0,
    createdBy: 'HIS Admin',
    createdOn: new Date(
      Date.now() - 86400000),
    lastUpdatedBy: 'HIS Admin',
    lastUpdatedOn: new Date(
      Date.now() - 86400000),
    tags: ['Draft', 'Legacy'],
    testCases: [],
    versions: [
      {
        id: 'v-hl7-1',
        versionNumber: '0.1.0',
        expression: HL7_JSONATA_EXPRESSION,
        createdBy: 'HIS Admin',
        createdOn: new Date(
          Date.now() - 86400000),
        status: 'Draft',
        changelog: 'Initial draft — ' +
          'MSH and RXO segment mapping',
        testStatus: 'NotTested',
        testsPassing: 0,
        testsTotal: 0,
        isActive: true
      }
    ]
  },
  {
    id: 'adapter-trimed-his',
    name: 'TriMed HIS Adapter',
    description:
      'Transforms CBCM regimen and order set objects into TriMed-compatible JSON payloads for ingestion into the TriMed Health Information System.',
    targetSystem: 'TriMed',
    outputFormat: 'JSON',
    status: 'Draft',
    currentVersion: '0.1.0',
    currentExpression: TRIMED_JSONATA_EXPRESSION,
    regimensDeployed: 0,
    testStatus: 'NotTested',
    testsPassing: 0,
    testsTotal: 0,
    createdBy: 'HIS Admin',
    createdOn: new Date(),
    lastUpdatedBy: 'HIS Admin',
    lastUpdatedOn: new Date(),
    tags: ['Trifour', 'TriMed', 'Draft'],
    testCases: [],
    versions: [
      {
        id: 'v-trimed-1',
        versionNumber: '0.1.0',
        expression: TRIMED_JSONATA_EXPRESSION,
        createdBy: 'HIS Admin',
        createdOn: new Date(),
        status: 'Draft',
        changelog: 'Initial draft — TriMed JSON payload mapping',
        testStatus: 'NotTested',
        testsPassing: 0,
        testsTotal: 0,
        isActive: true
      }
    ]
  }
]

export const MOCK_ADAPTER_STATS:
  AdapterSummaryStats = {
  totalAdapters: TRIFOUR_DEMO_MODE ? 1 : 4,
  activeAdapters: TRIFOUR_DEMO_MODE ? 0 : 2,
  draftAdapters: TRIFOUR_DEMO_MODE ? 1 : 2,
  failingTests: TRIFOUR_DEMO_MODE ? 0 : 3,
  totalRegimensDeployed: TRIFOUR_DEMO_MODE ? 0 : 24
}

export const MOCK_CBCM_SCHEMA:
  CbcmSchemaSection[] = [
  {
    id: 'identity',
    name: 'Regimen Identity',
    description: 'Core identifying ' +
      'fields of the regimen',
    icon: 'pi pi-id-card',
    fields: [
      {
        path: 'id',
        name: 'Regimen ID',
        type: 'string',
        description: 'Unique identifier ' +
          'for the regimen in Cyan Blue',
        required: true,
        example: 'rrg-mfolfox6-cco-2023',
        sourceSection: 'System generated',
        mappedFrom: 'System'
      },
      {
        path: 'regimenName',
        name: 'Regimen name',
        type: 'string',
        description: 'Short regimen ' +
          'abbreviation or name',
        required: true,
        example: 'MFOLFOX6',
        sourceSection: 'A · Overview',
        mappedFrom: 'Regimen Name'
      },
      {
        path: 'fullRegimenName',
        name: 'Full regimen name',
        type: 'string',
        description: 'Full drug combination ' +
          'name expanded',
        required: true,
        example: 'Folinic Acid-Fluorouracil' +
          '-Oxaliplatin',
        sourceSection: 'A · Overview',
        mappedFrom: 'Full Regimen Name'
      },
      {
        path: 'source',
        name: 'Source authority',
        type: 'string',
        description: 'Regulatory body or ' +
          'institution that owns this regimen',
        required: true,
        example: 'Cancer Care Ontario',
        sourceSection: 'System metadata',
        mappedFrom: 'Source'
      },
      {
        path: 'version',
        name: 'Version',
        type: 'string',
        description: 'Version identifier ' +
          'from the source authority',
        required: true,
        example: 'November 2023',
        sourceSection: 'System metadata',
        mappedFrom: 'Version'
      },
      {
        path: 'diseaseSite',
        name: 'Disease site',
        type: 'string',
        description: 'Primary disease ' +
          'site this regimen targets',
        required: true,
        example: 'Gastrointestinal',
        sourceSection: 'A · Overview',
        mappedFrom: 'Disease Site'
      },
      {
        path: 'cancerTypes',
        name: 'Cancer types',
        type: 'array',
        description: 'Specific cancer ' +
          'types within the disease site',
        required: true,
        example: ['Colorectal',
          'Small bowel and appendix'],
        sourceSection: 'A · Overview',
        mappedFrom: 'Cancer Types'
      },
      {
        path: 'intent',
        name: 'Treatment intent',
        type: 'string',
        description: 'Clinical intent of ' +
          'the treatment',
        required: true,
        example: 'Palliative',
        sourceSection: 'A · Overview',
        mappedFrom: 'Intent'
      }
    ]
  },
  {
    id: 'drugRegimen',
    name: 'Drug Regimen',
    description: 'Drug components, ' +
      'doses, routes and timing',
    icon: 'pi pi-list',
    fields: [
      {
        path: 'drugRegimen',
        name: 'Drug regimen array',
        type: 'array',
        description: 'Array of drug ' +
          'components in the regimen',
        required: true,
        example: [],
        sourceSection: 'B · Drug Regimen',
        mappedFrom: 'Drug Regimen table',
        children: [
          {
            path: 'drugRegimen[].order',
            name: 'Order / sequence',
            type: 'number',
            description: 'Administration ' +
              'sequence number',
            required: true,
            example: 1,
            sourceSection:
              'B · Drug Regimen',
            mappedFrom: 'Row order'
          },
          {
            path: 'drugRegimen[].drug',
            name: 'Drug name',
            type: 'string',
            description: 'Generic drug name',
            required: true,
            example: 'Oxaliplatin',
            sourceSection:
              'B · Drug Regimen',
            mappedFrom: 'Drug column'
          },
          {
            path: 'drugRegimen[].dose',
            name: 'Dose',
            type: 'string',
            description: 'Dose with unit ' +
              'e.g. 85 mg/m²',
            required: true,
            example: '85 mg/m²',
            sourceSection:
              'B · Drug Regimen',
            mappedFrom: 'Dose column'
          },
          {
            path: 'drugRegimen[].route',
            name: 'Route',
            type: 'string',
            description: 'Administration ' +
              'route',
            required: true,
            example: 'IV',
            sourceSection:
              'B · Drug Regimen',
            mappedFrom: 'Route column'
          },
          {
            path: 'drugRegimen[]' +
              '.administrationDuration',
            name: 'Duration',
            type: 'string',
            description: 'Infusion or ' +
              'administration duration',
            required: true,
            example: '2 hours',
            sourceSection:
              'B · Drug Regimen',
            mappedFrom: 'Duration column'
          },
          {
            path: 'drugRegimen[].timing',
            name: 'Timing',
            type: 'string',
            description: 'Day and sequence ' +
              'of administration',
            required: true,
            example: 'Day 1',
            sourceSection:
              'B · Drug Regimen',
            mappedFrom: 'Timing column'
          },
          {
            path: 'drugRegimen[].notes',
            name: 'Notes',
            type: 'string',
            description: 'Additional ' +
              'administration notes',
            required: false,
            example: 'Administer before ' +
              'fluorouracil',
            sourceSection:
              'B · Drug Regimen',
            mappedFrom: 'Notes column'
          }
        ]
      }
    ]
  },
  {
    id: 'cycleFrequency',
    name: 'Cycle Frequency',
    description: 'Cycle interval and ' +
      'continuation criteria',
    icon: 'pi pi-refresh',
    fields: [
      {
        path:
          'cycleFrequency.intervalDays',
        name: 'Cycle interval (days)',
        type: 'number',
        description: 'Number of days ' +
          'between cycles',
        required: true,
        example: 14,
        sourceSection:
          'C · Cycle Frequency',
        mappedFrom: 'Repeat interval'
      },
      {
        path:
          'cycleFrequency' +
          '.continuationCriteria',
        name: 'Continuation criteria',
        type: 'string',
        description: 'Clinical criteria ' +
          'for continuing treatment',
        required: true,
        example: 'Until disease ' +
          'progression or unacceptable ' +
          'toxicity',
        sourceSection:
          'C · Cycle Frequency',
        mappedFrom: 'Until statement'
      }
    ]
  },
  {
    id: 'clinicalMonitoring',
    name: 'Clinical Monitoring',
    description: 'Required and suggested ' +
      'monitoring parameters',
    icon: 'pi pi-heart',
    fields: [
      {
        path: 'clinicalMonitoring',
        name: 'Monitoring array',
        type: 'array',
        description: 'Array of monitoring ' +
          'parameters',
        required: true,
        example: [],
        sourceSection:
          'I · Clinical Monitoring',
        mappedFrom: 'Monitoring table',
        children: [
          {
            path:
              'clinicalMonitoring[].item',
            name: 'Monitoring item',
            type: 'string',
            description: 'Lab test or ' +
              'clinical assessment name',
            required: true,
            example: 'CBC',
            sourceSection:
              'I · Clinical Monitoring',
            mappedFrom:
              'Monitoring Item column'
          },
          {
            path:
              'clinicalMonitoring[].timing',
            name: 'Timing / frequency',
            type: 'string',
            description: 'When and how ' +
              'often to monitor',
            required: true,
            example:
              'Baseline and before ' +
              'each cycle',
            sourceSection:
              'I · Clinical Monitoring',
            mappedFrom: 'Timing column'
          },
          {
            path:
              'clinicalMonitoring[]' +
              '.category',
            name: 'Category',
            type: 'string',
            description: 'Required or ' +
              'Suggested',
            required: true,
            example: 'Required',
            sourceSection:
              'I · Clinical Monitoring',
            mappedFrom: 'Category column'
          }
        ]
      }
    ]
  },
  {
    id: 'administrativeInfo',
    name: 'Administrative Info',
    description: 'Workload and visit ' +
      'duration metrics',
    icon: 'pi pi-clock',
    fields: [
      {
        path: 'administrativeInfo' +
          '.approximateVisitDuration',
        name: 'Approximate visit duration',
        type: 'string',
        description: 'Total patient ' +
          'visit time estimate',
        required: false,
        example: '3 hours',
        sourceSection:
          'J · Administrative Info',
        mappedFrom:
          'Approximate Patient Visit'
      },
      {
        path: 'administrativeInfo' +
          '.pharmacyWorkloadMinutes',
        name: 'Pharmacy workload (minutes)',
        type: 'number',
        description: 'Average pharmacy ' +
          'time per visit in minutes',
        required: false,
        example: 38.38,
        sourceSection:
          'J · Administrative Info',
        mappedFrom: 'Pharmacy Workload'
      },
      {
        path: 'administrativeInfo' +
          '.nursingWorkloadMinutes',
        name: 'Nursing workload (minutes)',
        type: 'number',
        description: 'Average nursing ' +
          'time per visit in minutes',
        required: false,
        example: 69.17,
        sourceSection:
          'J · Administrative Info',
        mappedFrom: 'Nursing Workload'
      }
    ]
  }
]

// Sample CBCM JSON for the studio input panel — MFOLFOX6 (legacy adapter view)
export const MOCK_CBCM_MFOLFOX6 = CbcmUtils.toLegacyAdapterView(MOCK_CBCM_MFOLFOX6_DOC);


export const SAMPLE_REGIMENS: SampleRegimenOption[] = [
    {
        id: 'rrg-mfolfox6-cco-2023',
        name: 'MFOLFOX6 (CCO · November 2023)',
        data: MOCK_CBCM_MFOLFOX6 as unknown as Record<string, unknown>
    },
    {
        id: 'rrg-folfiri-beva-esmo',
        name: 'FOLFIRI + Bevacizumab (ESMO · v1.4)',
        data: {
            ...MOCK_CBCM_MFOLFOX6,
            id: 'rrg-folfiri-beva-esmo',
            regimenName: 'FOLFIRI-BEVA',
            fullRegimenName: 'Fluorouracil-Irinotecan-Folinic acid + Bevacizumab',
            source: 'ESMO',
            version: 'v1.4'
        } as unknown as Record<string, unknown>
    },
    {
        id: 'rrg-abvd-cco',
        name: 'ABVD (CCO · v5.0)',
        data: {
            ...MOCK_CBCM_MFOLFOX6,
            id: 'rrg-abvd-cco',
            regimenName: 'ABVD',
            fullRegimenName: 'Doxorubicin-Bleomycin-Vinblastine-Dacarbazine',
            diseaseSite: 'Lymphoma',
            cancerTypes: ['Classical Hodgkin lymphoma'],
            intent: 'Curative',
            drugRegimen: [
                { order: 1, drug: 'Doxorubicin', dose: '25 mg/m²', route: 'IV', administrationDuration: '15 minutes', timing: 'Days 1–15', notes: null },
                { order: 2, drug: 'Bleomycin', dose: '10 units/m²', route: 'IV', administrationDuration: '15 minutes', timing: 'Days 1–15', notes: null },
                { order: 3, drug: 'Vinblastine', dose: '6 mg/m²', route: 'IV', administrationDuration: '15 minutes', timing: 'Days 1–15', notes: null },
                { order: 4, drug: 'Dacarbazine', dose: '375 mg/m²', route: 'IV', administrationDuration: '60 minutes', timing: 'Days 1–15', notes: null }
            ]
        } as unknown as Record<string, unknown>
    }
];

export const SAMPLE_REGIMEN_BY_ID: Record<string, Record<string, unknown>> = Object.fromEntries(
    SAMPLE_REGIMENS.map((r) => [r.id, r.data])
);
